import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import intl from 'react-intl-universal';
import { useFlags } from 'launchdarkly-react-client-sdk';
import get from 'lodash/get';
import {
  selectAvailableLicenses,
  selectSelectedUser,
  updateUser,
  inviteUser,
  getLDUsers,
  getFacilitatorUsers,
  getAllUsers,
} from 'state/UsersManagement/usersManagementSlice';
import {
  displayNotification,
  setNotificationText,
  setNotificationVariant,
  setNotificationTimeout,
} from 'state/InlineNotification/inlineNotificationSlice';
import { USER_STATUS, USER_ACTIONS } from 'utils/constants';
import { AllUsersType, MoreActionsOption, License } from 'utils/customTypes';
import { shouldDeactivateNewUserForLicenses } from 'utils/functions';
import { selectOrganizationLicense } from 'state/Organization/organizationSlice';
import MoreActions from 'Organisms/MoreActions/MoreActions';
import EnableAccountToggle from './components/HeaderActions/EnableAccountToggle/EnableAccountToggle';
import DeleteUserConfirmation from './components/HeaderActions/DeleteUserConfirmation/DeleteUserConfirmation';

const UserPageHeader = ({ accountStatus }: { accountStatus: string }) => {
  const isAccountEnabled =
    accountStatus === USER_STATUS.INVITED ||
    accountStatus === USER_STATUS.REGISTERED;
  const dispatch = useDispatch();
  const selectedUser: AllUsersType = useSelector(selectSelectedUser);
  const availableLicenses = useSelector(selectAvailableLicenses);
  const licenseData: License = useSelector(selectOrganizationLicense);
  const { userDeletion = false } = useFlags<{ userDeletion: boolean }>();

  useEffect(() => {
    // selectAvailableLicenses derives the remaining seat count from the full
    // user list, so it must be loaded here too — otherwise the detail page
    // sees 0 active users and reports all seats available, defeating the
    // activation-toggle license guard below (CSI-744).
    dispatch(getAllUsers());
    dispatch(getLDUsers());
    dispatch(getFacilitatorUsers());
  }, [dispatch]);

  const handleEnableDisable = useCallback(async () => {
    const userStatus = get(selectedUser, 'status');
    switch (userStatus) {
      case USER_STATUS.REGISTERED:
      case USER_STATUS.INVITED:
        await dispatch(
          updateUser({
            userId: get(selectedUser, 'id'),
            updateFields: {
              disabled_at: new Date().toISOString(),
              status:
                userStatus === USER_STATUS.REGISTERED
                  ? USER_STATUS.REGISTERED_DISABLED
                  : USER_STATUS.INVITED_DISABLED,
            },
          })
        );
        break;
      case USER_STATUS.REGISTERED_DISABLED:
      case USER_STATUS.INVITED_DISABLED:
        await dispatch(
          updateUser({
            userId: get(selectedUser, 'id'),
            updateFields: {
              disabled_at: null,
              status:
                userStatus === USER_STATUS.REGISTERED_DISABLED
                  ? USER_STATUS.REGISTERED
                  : USER_STATUS.INVITED,
            },
          })
        );
        break;
    }
    dispatch(getLDUsers());
  }, [dispatch, selectedUser]);

  const toggleDisabled = useMemo(
    (): boolean =>
      !isAccountEnabled &&
      shouldDeactivateNewUserForLicenses(
        selectedUser.type,
        licenseData.license_tier,
        availableLicenses
      ),
    [
      availableLicenses,
      isAccountEnabled,
      selectedUser.type,
      licenseData.license_tier,
    ]
  );

  const options: MoreActionsOption[] = useMemo(() => {
    return [
      {
        iconName: 'mail',
        label: intl.get(
          'SETTINGS_PAGE.USER_PAGE.HEADER.MORE_ACTIONS.NOTIFY_USER'
        ),
        value: USER_ACTIONS.NOTIFY,
      },
    ];
  }, []);

  const onSelectOption = (option: MoreActionsOption) => {
    switch (option.value) {
      case USER_ACTIONS.NOTIFY:
        const userId = selectedUser.id;
        const email = selectedUser.data.email;
        const firstName = selectedUser.data.firstName;
        dispatch(
          inviteUser({
            userId,
            email,
            firstName,
            selfRegistration: firstName ? false : true,
          })
        );
        dispatch(setNotificationTimeout(4000));
        dispatch(setNotificationVariant('success'));
        dispatch(
          setNotificationText(intl.get('SETTINGS_PAGE.USER_PAGE.USER_NOTIFIED'))
        );
        dispatch(displayNotification());
        break;
      default:
        break;
    }
  };

  return (
    <div className='relative z-5'>
      <div className='h-8 flex items-center justify-end gap-x-2'>
        <EnableAccountToggle
          isAccountEnabled={isAccountEnabled}
          handleEnableDisable={handleEnableDisable}
          toggleDisabled={toggleDisabled}
        />
        {userDeletion && <DeleteUserConfirmation />}
        {accountStatus === USER_STATUS.INVITED && (
          <MoreActions options={options} onSelectOption={onSelectOption} />
        )}
      </div>
    </div>
  );
};

export default UserPageHeader;
